

export const dynamic = 'force-dynamic';

import { useState } from "react";
import { useLocation, Link } from 'react-router-dom';
import DashboardLayout from "@/app/components/DashboardLayout";
import { formatDate } from "@/lib/dateFormat";
import { localToday } from "@/lib/data";
import { fyRange, monthRange, quarterRange, useCanSeeMoney, yearRange } from "../ReportKit";

// ─── Period helpers ───────────────────────────────────────────────────────────

type PeriodFilter = "month" | "last" | "3months" | "quarter" | "fy" | "lastfy" | "year" | "lastyear" | "custom";
export type Period = { from: string; to: string };

const periodOptions: { id: PeriodFilter; label: string }[] = [
  { id: "month", label: "This month" },
  { id: "last", label: "Last month" },
  { id: "3months", label: "Last 3 months" },
  { id: "quarter", label: "This quarter" },
  { id: "fy", label: "This financial year" },
  { id: "lastfy", label: "Last financial year" },
  { id: "year", label: "This calendar year" },
  { id: "lastyear", label: "Last calendar year" },
  { id: "custom", label: "Custom" },
];

function getPeriodDates(period: Exclude<PeriodFilter, "custom">): Period {
  const today = localToday();
  switch (period) {
    case "last": return monthRange(-1);
    case "3months": return { from: monthRange(-2).from, to: monthRange(0).to };
    case "quarter": return quarterRange(0);
    case "fy": return fyRange(0);
    case "lastfy": return fyRange(-1);
    case "year": return yearRange(0);
    case "lastyear": return yearRange(-1);
    default: return monthRange(0, today);
  }
}

const PERIOD_KEY = "vendor-reports-period";

/** The chosen period, shared by the four vendor tabs (starts as this whole month). */
export function useVendorPeriod(): [Period, (p: Period) => void] {
  const [period, setPeriod] = useState<Period>(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(PERIOD_KEY) || "null");
      if (saved?.from && saved?.to) return saved;
    } catch { /* no saved period */ }
    return getPeriodDates("month");
  });
  const update = (p: Period) => {
    setPeriod(p);
    try { sessionStorage.setItem(PERIOD_KEY, JSON.stringify(p)); } catch { /* storage unavailable */ }
  };
  return [period, update];
}

// ─── Navigation tabs ──────────────────────────────────────────────────────────

const navTabs = [
  { id: "performance", label: "Performance", href: "/reports/vendors/performance", money: false },
  { id: "assignments", label: "Assignments", href: "/reports/vendors/assignments", money: false },
  { id: "commissions", label: "Commissions", href: "/reports/vendors/commissions", money: true },
  { id: "comparison", label: "Comparison", href: "/reports/vendors/comparison", money: false },
];

interface VendorReportsLayoutProps {
  children: React.ReactNode;
  period: Period;
  onPeriodChange: (period: Period) => void;
  /** Shown beside the heading, e.g. the Excel / Print buttons */
  actions?: React.ReactNode;
}

export default function VendorReportsLayout({ children, period, onPeriodChange, actions }: VendorReportsLayoutProps) {
  const canSeeMoney = useCanSeeMoney();
  const location = useLocation();

  const [activePeriod, setActivePeriod] = useState<PeriodFilter>(() => {
    const match = periodOptions.find((p) => p.id !== "custom" && JSON.stringify(getPeriodDates(p.id as Exclude<PeriodFilter, "custom">)) === JSON.stringify(period));
    return match?.id ?? "custom";
  });
  const [customStart, setCustomStart] = useState(period.from);
  const [customEnd, setCustomEnd] = useState(period.to);
  const [customError, setCustomError] = useState("");

  const activeTab = navTabs.find((tab) => location.pathname?.includes(tab.id))?.id || "performance";

  const handlePeriodClick = (id: PeriodFilter) => {
    setActivePeriod(id);
    if (id !== "custom") onPeriodChange(getPeriodDates(id));
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) { setCustomError("Pick both dates."); return; }
    if (customEnd < customStart) { setCustomError("The end date is before the start date."); return; }
    setCustomError("");
    onPeriodChange({ from: customStart, to: customEnd });
  };

  const tabs = navTabs.filter((tab) => !tab.money || canSeeMoney);

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-stone-800 font-serif">Vendor Reports</h2>
            <p className="text-xs text-stone-500">
              Work on events that start between {formatDate(period.from)} and {formatDate(period.to)}
            </p>
          </div>
          {actions}
        </div>

        <div className="flex gap-1 border-b border-stone-200 overflow-x-auto">
          {tabs.map((tab) => (
            <Link
              key={tab.id}
              to={tab.href}
              className={`shrink-0 px-4 h-10 inline-flex items-center text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-amber-500 text-amber-700"
                  : "border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300"
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </div>

        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 pb-1">
          {periodOptions.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePeriodClick(p.id)}
              aria-pressed={activePeriod === p.id}
              className={`shrink-0 h-9 px-3 rounded-lg text-xs font-medium transition-colors border ${
                activePeriod === p.id
                  ? "bg-amber-50 border-amber-300 text-amber-700"
                  : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {activePeriod === "custom" && (
          <div className="flex flex-wrap items-end gap-3 p-3 bg-stone-50 rounded-xl border border-stone-200">
            <label className="text-xs text-stone-500 flex flex-col gap-1">
              From
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                className="h-10 px-3 rounded-lg border border-stone-200 text-sm focus:border-amber-400 outline-none bg-white" />
            </label>
            <label className="text-xs text-stone-500 flex flex-col gap-1">
              To
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                className="h-10 px-3 rounded-lg border border-stone-200 text-sm focus:border-amber-400 outline-none bg-white" />
            </label>
            <button type="button" onClick={applyCustom}
              className="h-10 px-4 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600">
              Apply
            </button>
            {customError && <p className="text-xs text-red-600 w-full">{customError}</p>}
          </div>
        )}

        {children}
      </div>
    </DashboardLayout>
  );
}
